/** Kundendisplay (E11.2): zweites Gerät oder Tab zeigt den Warenkorb live – ohne Mieternamen, groß lesbar, „Danke“ nach Zahlung. */
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Icon, Seitenzustand } from "../ui";
import { Marke } from "../ui/Marke";
import { api, rufe } from "../api/client";
import { formatEuro } from "@shared/geld";

interface DisplayDaten {
  geraet: string;
  laden: { name: string; slug: string };
  warenkorb: { id: string; status: string; summeCent: number; uebung: boolean; bezahltAt: string | null; positionen: { id: string; bezeichnung: string; preisCent: number; rabattCent: number; endpreisCent: number; art: string }[] } | null;
}

export function Display() {
  const { code = "" } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["display", code], queryFn: () => rufe<DisplayDaten>(api.api.public.display[":code"].$get({ param: { code } })), refetchInterval: 15_000, retry: false });
  const [verbunden, setVerbunden] = useState(false);
  useEffect(() => {
    let ws: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let beendet = false;
    const verbinde = () => {
      if (beendet) return;
      ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/api/public/display/${code}/ws`);
      ws.onopen = () => setVerbunden(true);
      ws.onmessage = () => void qc.invalidateQueries({ queryKey: ["display", code] });
      ws.onclose = () => {
        setVerbunden(false);
        if (!beendet) timer = setTimeout(verbinde, 3000);
      };
      ws.onerror = () => ws?.close();
    };
    verbinde();
    const ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send("ping"), 25_000);
    return () => {
      beendet = true;
      clearInterval(ping);
      if (timer) clearTimeout(timer);
      ws?.close();
    };
  }, [code, qc]);

  return (
    <div className="display">
      <Seitenzustand query={q} lines={5}>
        {(d) => {
          const wk = d.warenkorb;
          const bezahlt = wk?.status === "bezahlt";
          const leer = !wk || wk.positionen.length === 0 || wk.status === "abgebrochen";
          return (
            <>
              <header className="display__kopf">
                <Marke size={44} />
                <span className="display__laden">{d.laden.name}</span>
                <span className="sc-grow" />
                {wk?.uebung ? <span className="sc-pill">Übung</span> : null}
                <span className="sc-muted sc-small">{verbunden ? "live" : "…"}</span>
              </header>
              {bezahlt ? (
                <main className="display__mitte">
                  <span className="sc-success-ring" aria-hidden="true"><Icon name="check" size={72} strokeWidth={3} /></span>
                  <h1 className="display__gross">Danke!</h1>
                  <p className="display__text">{formatEuro(wk!.summeCent)} · Schön, dass du da warst.</p>
                </main>
              ) : leer ? (
                <main className="display__mitte">
                  <h1 className="display__gross">Willkommen</h1>
                  <p className="display__text">Gleich geht’s los.</p>
                </main>
              ) : (
                <main className="display__liste">
                  <ul>
                    {wk!.positionen.map((p) => (
                      <li key={p.id} className={p.endpreisCent < 0 ? "is-abzug" : undefined}>
                        <span className="display__name">{p.bezeichnung}</span>
                        <span className="display__preis">{p.rabattCent > 0 ? <s className="sc-muted">{formatEuro(p.preisCent)}</s> : null} {formatEuro(p.endpreisCent)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="display__summe">
                    <span>{wk!.status === "offen" ? "Zwischensumme" : "Zu zahlen"}</span>
                    <span>{formatEuro(wk!.summeCent)}</span>
                  </div>
                  {wk!.status !== "offen" ? <p className="display__text">Bitte am SumUp-Gerät bezahlen – bar oder Karte.</p> : null}
                </main>
              )}
            </>
          );
        }}
      </Seitenzustand>
    </div>
  );
}
